import { useState } from 'react';
import {
  Button,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import type { ServiceSla } from '@/api/services';
import { useUpsertServiceSla, type ServiceSlaPayload } from '@/api/services-mutations';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

function num(v: string): number | undefined {
  const n = Number(v);
  return v === '' || Number.isNaN(n) ? undefined : n;
}

/**
 * Manual config — set the core SLA terms directly (uptime %, response /
 * resolution time, effective window, business-hours scope) without a template.
 * Writes `POST|PUT /services/:id/sla` with `sla_source: manual`.
 */
export function SlaManualConfigDialog({
  serviceId,
  existing,
  open,
  onOpenChange,
}: {
  serviceId: number;
  existing?: ServiceSla | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const upsert = useUpsertServiceSla(serviceId, !!existing);
  const defaultRule = existing?.sla_rules?.find((r) => r.ticket_type_group === 'default');

  const [uptime, setUptime] = useState(String(existing?.uptime_guarantee ?? ''));
  const [response, setResponse] = useState(
    String(defaultRule?.response_time_minutes ?? existing?.template_default_response_time ?? ''),
  );
  const [resolution, setResolution] = useState(
    String(defaultRule?.resolution_time_minutes ?? existing?.template_default_resolution_time ?? ''),
  );
  const [allowance, setAllowance] = useState(String(existing?.downtime_allowance_minutes ?? ''));
  const [from, setFrom] = useState(existing?.effective_from ?? '');
  const [to, setTo] = useState(existing?.effective_to ?? '');
  const [businessHours, setBusinessHours] = useState(!!existing?.business_hours_only);

  if (!open) return null;

  const submit = () => {
    const payload: ServiceSlaPayload = {
      uptime_guarantee: num(uptime),
      downtime_allowance_minutes: num(allowance),
      business_hours_only: businessHours,
      effective_from: from || undefined,
      effective_to: to || undefined,
    };
    const resp = num(response);
    const reso = num(resolution);
    if (resp != null || reso != null) {
      payload.sla_rules = [
        { ticket_type_group: 'default', response_time_minutes: resp ?? 0, resolution_time_minutes: reso ?? 0 },
      ];
    }
    upsert.mutate(payload, {
      onSuccess: () => { toast.success('SLA config saved'); onOpenChange(false); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit SLA config' : 'Manual SLA config'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Uptime guarantee (%)">
              {(p) => <NumericInput {...p} step="0.01" value={uptime} onChange={(e) => setUptime(e.target.value)} placeholder="99.9" />}
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Response time (min)">
                {(p) => <NumericInput {...p} value={response} onChange={(e) => setResponse(e.target.value)} placeholder="120" />}
              </Field>
              <Field label="Resolution time (min)">
                {(p) => <NumericInput {...p} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="480" />}
              </Field>
            </div>
            <Field label="Downtime allowance / month (min)">
              {(p) => <NumericInput {...p} value={allowance} onChange={(e) => setAllowance(e.target.value)} placeholder="43" />}
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Effective from">
                {(p) => <DateInput {...p} value={from} onChange={(e) => setFrom(e.target.value)} />}
              </Field>
              <Field label="Effective to">
                {(p) => <DateInput {...p} value={to} onChange={(e) => setTo(e.target.value)} />}
              </Field>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Switch checked={businessHours} onCheckedChange={setBusinessHours} />
              <span>Business hours only (otherwise 24/7)</span>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={upsert.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
